'use client';

import { memo, useMemo } from 'react';

import { SBox, STypography } from 'components/ui';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';

import { BatteryState_PowerEnum } from 'lib/generated/proto/sensor_msgs/msg/BatteryState';
import { useModelName } from 'lib/api/myRobot';
import { useStatusPowerPolicy } from 'hooks/statekeeping';
import { useBattery } from 'lib/ros-abstraction';

import { SBatteryIcon, SChargeIcon, SIconContainer } from './BatteryStatus.styled';

const POWER_STATUS_LABELS: Partial<Record<BatteryState_PowerEnum, string>> = {
  [BatteryState_PowerEnum.POWER_SUPPLY_STATUS_CHARGING]: 'Charging',
  [BatteryState_PowerEnum.POWER_SUPPLY_STATUS_DISCHARGING]: 'Discharging',
  [BatteryState_PowerEnum.POWER_SUPPLY_STATUS_FULL]: 'Full',
  [BatteryState_PowerEnum.POWER_SUPPLY_STATUS_NOT_CHARGING]: 'Not Charging',
  [BatteryState_PowerEnum.POWER_SUPPLY_STATUS_UNKNOWN]: 'Unknown',
};

export type BatteryStatusDisplayProps = {
  percentage: number;
  status?: BatteryState_PowerEnum;
  voltage?: number;
  temperature?: number;
  returnHomePower?: number;
  landingPower?: number;
  isDrone?: boolean;
  remainingRuntime?: { seconds?: number; nanos?: number } | null;
};

/**
 * Pure presentational battery component. Use this when outside a [robotId] route context
 * (e.g. home page robot cards, fleet map popups, test pages).
 */
export const BatteryStatusDisplay = memo<BatteryStatusDisplayProps>(
  ({
    percentage,
    status = BatteryState_PowerEnum.POWER_SUPPLY_STATUS_UNKNOWN,
    voltage,
    temperature,
    returnHomePower,
    landingPower,
    isDrone = false,
    remainingRuntime,
  }) => {
    const getBatteryDetails = useMemo(() => {
      const details: string[] = [];

      details.push(`Battery: ${Math.round(percentage)}%`);

      const statusLabel = POWER_STATUS_LABELS[status];
      if (statusLabel) {
        details.push(`Status: ${statusLabel}`);
      }

      if (remainingRuntime?.seconds && Number(remainingRuntime.seconds) > 0) {
        const seconds = Number(remainingRuntime.seconds);
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);

        if (hours > 0) {
          details.push(`Runtime: ${hours}h ${minutes}m`);
        } else {
          details.push(`Runtime: ${minutes}m`);
        }
      }

      if (voltage !== undefined) {
        details.push(`Voltage: ${voltage.toFixed(2)} V`);
      }
      if (temperature !== undefined) {
        details.push(`Temperature: ${temperature.toFixed(1)} °C`);
      }

      if (returnHomePower !== undefined) {
        const returnHomeStatus = percentage > returnHomePower ? '✓' : '⚠️';
        details.push(`${returnHomeStatus} Return Home: ${returnHomePower}%`);
      }

      if (isDrone && landingPower !== undefined) {
        const landingStatus = percentage > landingPower ? '✓' : '⚠️';
        details.push(`${landingStatus} Landing Power: ${landingPower}%`);
      }

      if (isDrone && landingPower !== undefined && percentage <= landingPower) {
        details.push('🔴 Critical: Below landing power!');
      } else if (returnHomePower !== undefined && percentage <= returnHomePower) {
        details.push('🟡 Warning: Below return home power!');
      }

      return details;
    }, [percentage, status, voltage, temperature, returnHomePower, landingPower, isDrone, remainingRuntime]);

    const batteryColor = useMemo(() => {
      switch (true) {
        case percentage >= 70 && percentage <= 100:
          return 'green';
        case percentage >= 30 && percentage < 70:
          return 'yellow';
        case percentage >= 0 && percentage < 30:
          return 'red';
        default:
          return 'neutral';
      }
    }, [percentage]);

    const isBatteryAvailable = percentage !== undefined && percentage >= 0;

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <SBox display="inline-flex" align="center">
              <SIconContainer level={percentage} color={batteryColor}>
                <SBatteryIcon icon="battery_0" color={batteryColor} />
                {status === BatteryState_PowerEnum.POWER_SUPPLY_STATUS_CHARGING && <SChargeIcon icon="bolt" />}
              </SIconContainer>
              <STypography variant="subtitle2" color={batteryColor}>
                <b>{isBatteryAvailable ? `${Math.round(percentage)}%` : '~'}</b>
              </STypography>
            </SBox>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <div className="space-y-1">
              <div className="font-semibold text-sm">Battery Details</div>
              {getBatteryDetails.map((detail, index) => (
                <div key={index} className="text-xs">
                  {detail}
                </div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  },
);

BatteryStatusDisplay.displayName = 'BatteryStatusDisplay';

type BatteryStatusProps = {
  /** Override battery percentage (0–100). When omitted, useBattery() provides the value. */
  percentage?: number;
  /** Override power status enum. When omitted, useBattery() provides the value. */
  status?: BatteryState_PowerEnum;
  /** Override voltage in Volts. When omitted, useBattery() provides the value. */
  voltage?: number;
  /** Override temperature in °C. When omitted, useBattery() provides the value. */
  temperature?: number;
  /** Override return home power threshold. When omitted, useStatusPowerPolicy() provides the value. */
  returnHomePower?: number;
  /** Override landing power threshold. When omitted, useStatusPowerPolicy() provides the value. */
  landingPower?: number;
  /** Override drone flag. When omitted, useModelName() provides the value. */
  isDrone?: boolean;
  remainingRuntime?: { seconds?: number; nanos?: number } | null;
};

/**
 * Route-aware battery component. Must only be used inside a [robotId] route segment.
 * For use outside a route context, use BatteryStatusDisplay instead.
 */
const BatteryStatus = memo<BatteryStatusProps>(
  ({ percentage, status, voltage, temperature, returnHomePower, landingPower, isDrone: isDroneProp, remainingRuntime }) => {
    const { battery } = useBattery();
    const { powerPolicy } = useStatusPowerPolicy();
    const { isDrone } = useModelName();

    return (
      <BatteryStatusDisplay
        percentage={percentage ?? battery.percentage}
        status={status ?? battery.powerStatus}
        voltage={voltage ?? battery.voltage}
        temperature={temperature ?? battery.temperature}
        returnHomePower={returnHomePower ?? powerPolicy?.returnHomePower}
        landingPower={landingPower ?? powerPolicy?.landingPower}
        isDrone={isDroneProp ?? isDrone}
        remainingRuntime={remainingRuntime}
      />
    );
  },
);

BatteryStatus.displayName = 'BatteryStatus';

export default BatteryStatus;
